import React from "react";
import {
  AppBar as MUIAppBar,
  Toolbar,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  alpha,
  useTheme,
} from "@mui/material";
import { useNavigate, Link } from "react-router-dom";
import Logo from "../assets/app-bar-logo.png";
import HistoryIcon from "@mui/icons-material/History";
import LogoutIcon from "@mui/icons-material/Logout";
import useCustomMediaQuery from "../shared/mediaQuery/useCustomMediaQuery";
import { logoutUser } from "../shared/utils/authUser";
import ShareButton from "../shared/components/ShareButton";

const CustomAppBar: React.FC = () => {
  const { isSmall } = useCustomMediaQuery();
  const theme = useTheme();
  const baseURL = window.location.protocol + "//" + window.location.hostname;
  const navigate = useNavigate();

  // State for managing the menu
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Handlers for menu
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCallHistory = () => {
    handleMenuClose();
    navigate("/call-history");
  };

  const handleLogout = () => {
    logoutUser();
    navigate("/login");
  };

  return (
    <MUIAppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: 1300,
        background: `linear-gradient(135deg, 
          ${theme.palette.grey[100]} 0%, 
          ${theme.palette.grey[100]} 50%, 
          ${alpha(theme.palette.grey[100], 0.9)} 100%)`,
        backdropFilter: "blur(20px) saturate(1.8)",
        borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
        boxShadow: `
          0 0px 5px ${alpha(theme.palette.grey[900], 0.3)},
          0 4px 16px ${alpha(theme.palette.grey[800], 0.2)},
          inset 0 1px 0 ${alpha(theme.palette.common.white, 0.2)}
        `,
        // Use safe-area-inset-top for iOS notch
        paddingTop: "env(safe-area-inset-top, 0px)",
        // Ensure height accounts for padding
        minHeight: 64,
        height: `calc(64px + env(safe-area-inset-top, 0px))`,
        boxSizing: "border-box",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(90deg, 
            transparent 0%, 
            ${alpha(theme.palette.common.white, 0.1)} 50%, 
            transparent 100%)`,
          opacity: 0,
          transition: "opacity 0.3s ease",
        },
        "&:hover::before": {
          opacity: 1,
        },
      }}
    >
      <Toolbar
        sx={{
          position: "relative",
          minHeight: "64px !important",
          alignItems: "center",
          px: { xs: 2, sm: 3, md: 4 },
          py: 1,
        }}
      >
        {/* Logo and Brand Section */}
        <Box 
          display="flex" 
          alignItems="center" 
          flexGrow={1}
          sx={{
            transition: "transform 0.2s ease",
          }}
        >
          <Link
            to="/dashboard"
            style={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              padding: "8px 12px",
              borderRadius: "12px",
              transition: "all 0.3s ease",
              background: `linear-gradient(135deg, 
                ${alpha(theme.palette.primary.light, 0.15)} 0%, 
                ${alpha(theme.palette.primary.light, 0.2)} 100%)`,
              backdropFilter: "blur(10px)",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
            }}
          >
            <Box
              sx={{
                width: 41,
                height: 37,
                borderRadius: "10px",
                background: `linear-gradient(135deg, 
                  ${theme.palette.primary.main} 0%, 
                  ${theme.palette.primary.dark} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 1,
                boxShadow: `
                  0 4px 12px ${alpha(theme.palette.primary.main, 0.4)},
                  inset 0 1px 0 ${alpha(theme.palette.common.white, 0.3)}
                `,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "scale(1.1) rotate(5deg)",
                  boxShadow: `
                    0 6px 20px ${alpha(theme.palette.primary.main, 0.5)},
                    inset 0 1px 0 ${alpha(theme.palette.common.white, 0.4)}
                  `,
                },
              }}
            >
              <img
                src={Logo}
                alt="Company Logo"
                style={{ 
                  height: 28,
                  width: 28,
                  objectFit: "contain",
                }}
              />
            </Box>
            <Typography 
              variant="h6" 
              sx={{
                color: theme.palette.common.white,
                fontWeight: 700,
                fontSize: { xs: "1.1rem", sm: "1.25rem" },
                letterSpacing: "0.02em",
                //textShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.5)}`,
                background: `linear-gradient(135deg, 
                  ${theme.palette.primary.dark} 0%, 
                  ${theme.palette.primary.light} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Musadan
            </Typography>
          </Link>
        </Box>

        {/* Actions Section */}
        <Box 
          display="flex" 
          alignItems="center" 
          gap={1}
          sx={{
            "& > *": {
              transition: "all 0.3s ease",
            },
          }}
        >
          {/* Share Button with enhanced styling */}
          <Box
            sx={{
              "& button": {
                background: theme.palette.grey[200],
                backdropFilter: "blur(10px)",
                border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                color: "#000000",
                "&:hover": {
                  background: theme.palette.grey[200],
                  color: theme.palette.primary.main,
                },
              },
            }}
          >
            <ShareButton url={baseURL} />
          </Box>

          {/* Avatar Button with enhanced styling */}
          <IconButton 
            onClick={handleMenuOpen} 
            size="large"
            sx={{
              p: 0.5,
              background: theme.palette.grey[200],
              backdropFilter: "blur(10px)",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                background: theme.palette.grey[200],
                transform: "translateY(-2px) scale(1.05)",
              },
            }}
          >
            <Avatar 
              alt="User Avatar"
              sx={{
                width: 40,
                height: 40,
                background: `linear-gradient(135deg, 
                  ${theme.palette.secondary.main} 0%, 
                  ${theme.palette.secondary.dark} 100%)`,
                color: theme.palette.common.white,
                fontWeight: 600,
                fontSize: "1rem",
                boxShadow: `inset 0 2px 4px ${alpha(theme.palette.common.black, 0.2)}`,
              }}
            />
          </IconButton>
        </Box>

        {/* Enhanced Menu */}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: 3,
              minWidth: 200,
              background: `linear-gradient(145deg, 
                ${alpha(theme.palette.background.paper, 0.95)} 0%, 
                ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
              backdropFilter: "blur(20px) saturate(1.8)",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              boxShadow: `
                0 16px 40px ${alpha(theme.palette.common.black, 0.15)},
                0 8px 20px ${alpha(theme.palette.common.black, 0.1)},
                inset 0 1px 0 ${alpha(theme.palette.common.white, 0.2)}
              `,
              "& .MuiMenuItem-root": {
                borderRadius: 2,
                mx: 1,
                my: 0.5,
                transition: "all 0.2s ease",
                "&:hover": {
                  background: `linear-gradient(135deg, 
                    ${alpha(theme.palette.primary.main, 0.1)} 0%, 
                    ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                  transform: "translateX(4px)",
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                },
                "& .MuiListItemIcon-root": {
                  color: theme.palette.primary.main,
                  minWidth: 36,
                },
                "& .MuiListItemText-primary": {
                  fontWeight: 500,
                  fontSize: "0.95rem",
                },
              },
            },
          }}
        >
          {/* <MenuItem onClick={handleCallHistory}>
            <ListItemIcon>
              <HistoryIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Call History</ListItemText>
          </MenuItem> */}
          <MenuItem 
            onClick={handleLogout}
            sx={{
              "&:hover": {
                background: `linear-gradient(135deg, 
                  ${alpha(theme.palette.error.main, 0.1)} 0%, 
                  ${alpha(theme.palette.error.main, 0.05)} 100%) !important`,
                "& .MuiListItemIcon-root": {
                  color: theme.palette.error.main,
                },
              },
            }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        </Menu>
      </Toolbar>
    </MUIAppBar>
  );
};

export default CustomAppBar;
